import React, { useEffect, useState, useCallback } from 'react';
import {
  FiUsers, FiGrid, FiUserCheck, FiLock, FiGlobe, FiBriefcase,
  FiPlus, FiTrash2, FiRefreshCw, FiShield,
} from 'react-icons/fi';
import {
  getProjectAccess, setProjectVisibility, addProjectMember, removeProjectMember,
  addProjectGroup, removeProjectGroup, addProjectTeam, removeProjectTeam,
  PERMISSION_LABELS,
  type ProjectAccess, type Permission, type Visibility,
} from '../../api/projectAccess';
import apiClient from '../../api/client';

interface Props { projectId: string; canManage: boolean; }

const ALL_PERMISSIONS: Permission[] = ['read', 'write', 'delete', 'deploy', 'manage_projects', 'manage_users', 'admin'];

const VISIBILITY_OPTIONS: { value: Visibility; label: string; desc: string; icon: React.FC<any> }[] = [
  { value: 'private', label: 'Privé',            desc: 'Seuls les membres explicitement ajoutés', icon: FiLock },
  { value: 'org',     label: 'Organisation',     desc: 'Tous les membres de l\'organisation peuvent lire', icon: FiBriefcase },
  { value: 'public',  label: 'Public',           desc: 'Accessible en lecture sans authentification', icon: FiGlobe },
];

// Checkbox liste de permissions
function PermCheckList({ selected, onChange }: { selected: Permission[]; onChange: (p: Permission[]) => void }) {
  const toggle = (p: Permission) =>
    onChange(selected.includes(p) ? selected.filter(x => x !== p) : [...selected, p]);
  return (
    <div className="grid grid-cols-2 gap-1 mt-2">
      {ALL_PERMISSIONS.map(p => (
        <label key={p} className="flex items-center gap-2 text-xs cursor-pointer select-none">
          <input type="checkbox" checked={selected.includes(p)} onChange={() => toggle(p)}
            className="accent-blue-600 w-3 h-3" />
          <span className="text-gray-700">{PERMISSION_LABELS[p]}</span>
        </label>
      ))}
    </div>
  );
}

// Badge permission
function PermBadge({ perm }: { perm: Permission }) {
  const colors: Record<string, string> = {
    read: 'bg-gray-100 text-gray-600', write: 'bg-blue-50 text-blue-700',
    delete: 'bg-red-50 text-red-700', deploy: 'bg-green-50 text-green-700',
    manage_projects: 'bg-purple-50 text-purple-700',
    manage_users: 'bg-orange-50 text-orange-700', admin: 'bg-yellow-50 text-yellow-800',
  };
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${colors[perm] || 'bg-gray-100 text-gray-600'}`}>
      {PERMISSION_LABELS[perm]}
    </span>
  );
}

export const ProjectAccessPanel: React.FC<Props> = ({ projectId, canManage }) => {
  const [access, setAccess] = useState<ProjectAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'members' | 'groups' | 'teams'>('members');

  // Listes de l'organisation pour les selects
  const [orgUsers, setOrgUsers]   = useState<any[]>([]);
  const [orgGroups, setOrgGroups] = useState<any[]>([]);
  const [orgTeams, setOrgTeams]   = useState<any[]>([]);

  // Formulaires d'ajout
  const [addMemberOpen,  setAddMemberOpen]  = useState(false);
  const [addGroupOpen,   setAddGroupOpen]   = useState(false);
  const [addTeamOpen,    setAddTeamOpen]    = useState(false);

  const [selUser,   setSelUser]   = useState('');
  const [selGroup,  setSelGroup]  = useState('');
  const [selTeam,   setSelTeam]   = useState('');
  const [selMPerms, setSelMPerms] = useState<Permission[]>(['read']);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProjectAccess(projectId);
      setAccess(data);
    } catch { setError('Impossible de charger les accès'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canManage) return;
    Promise.all([
      apiClient.get('/api/users').catch(() => ({ data: { users: [] } })),
      apiClient.get('/api/groups').catch(() => ({ data: { groups: [] } })),
      apiClient.get('/api/teams').catch(() => ({ data: { teams: [] } })),
    ]).then(([u, g, t]) => {
      setOrgUsers(u.data.users  || u.data.members || []);
      setOrgGroups(g.data.groups || []);
      setOrgTeams(t.data.teams   || []);
    });
  }, [canManage]);

  async function handleSetVisibility(v: Visibility) {
    if (!access) return;
    try {
      await setProjectVisibility(projectId, v);
      setAccess({ ...access, visibility: v });
    } catch { setError('Erreur lors du changement de visibilité'); }
  }

  async function handleAddMember() {
    if (!selUser || selMPerms.length === 0) return;
    setSaving(true); setError(null);
    try {
      await addProjectMember(projectId, selUser, selMPerms);
      setSelUser(''); setSelMPerms(['read']); setAddMemberOpen(false);
      await load();
    } catch (e: any) { setError(e?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  }

  async function handleAddGroup() {
    if (!selGroup) return;
    setSaving(true); setError(null);
    try {
      await addProjectGroup(projectId, selGroup);
      setSelGroup(''); setAddGroupOpen(false);
      await load();
    } catch (e: any) { setError(e?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  }

  async function handleAddTeam() {
    if (!selTeam) return;
    setSaving(true); setError(null);
    try {
      await addProjectTeam(projectId, selTeam);
      setSelTeam(''); setAddTeamOpen(false);
      await load();
    } catch (e: any) { setError(e?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  }

  if (loading) return (
    <div className="animate-pulse space-y-3 p-4">
      {[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
    </div>
  );

  if (!access) return <div className="p-4 text-red-500 text-sm">{error || 'Erreur de chargement'}</div>;

  const memberIds  = new Set(access.members.map(m => m.user_id));
  const groupIds   = new Set(access.groups.map(g => g.group_id));
  const teamIds    = new Set(access.teams.map(t => t.team_id));

  return (
    <div className="space-y-5">
      {/* Visibilité */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <FiShield size={14} /> Visibilité du projet
        </h3>
        <div className="flex gap-2">
          {VISIBILITY_OPTIONS.map(({ value, label, desc, icon: Icon }) => (
            <button
              key={value}
              disabled={!canManage}
              onClick={() => handleSetVisibility(value)}
              className={`flex-1 p-3 rounded-lg border text-left transition-all ${
                access.visibility === value
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              } disabled:opacity-50 disabled:cursor-default`}
            >
              <Icon size={14} className="mb-1" />
              <p className="text-xs font-semibold">{label}</p>
              <p className="text-xs text-gray-500 leading-tight mt-0.5">{desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex border-b border-gray-100">
          {([
            { key: 'members', label: `Membres (${access.members.length})`,   icon: FiUsers },
            { key: 'groups',  label: `Groupes (${access.groups.length})`,    icon: FiGrid },
            { key: 'teams',   label: `Équipes (${access.teams.length})`,     icon: FiUserCheck },
          ] as const).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                tab === key
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        <div className="p-4">
          {error && <p className="text-red-500 text-xs mb-3">{error}</p>}

          {/* ── MEMBRES ── */}
          {tab === 'members' && (
            <div className="space-y-2">
              {access.members.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">Aucun membre direct</p>
              )}
              {access.members.map(m => (
                <div key={m.user_id} className="flex items-center justify-between py-2 border-b border-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{m.full_name}</p>
                    <p className="text-xs text-gray-400">{m.email}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {m.permissions.map(p => <PermBadge key={p} perm={p} />)}
                    </div>
                  </div>
                  {canManage && (
                    <button onClick={() => removeProjectMember(projectId, m.user_id).then(load)}
                      className="p-1 text-red-400 hover:text-red-600">
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </div>
              ))}

              {canManage && (
                addMemberOpen ? (
                  <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                    <select value={selUser} onChange={e => setSelUser(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400">
                      <option value="">Choisir un utilisateur...</option>
                      {orgUsers.filter(u => !memberIds.has(u.id)).map(u => (
                        <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
                      ))}
                    </select>
                    <PermCheckList selected={selMPerms} onChange={setSelMPerms} />
                    <div className="flex gap-2 pt-1">
                      <button onClick={handleAddMember} disabled={saving || !selUser}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg disabled:opacity-50">
                        {saving ? <FiRefreshCw size={11} className="animate-spin" /> : <FiPlus size={11} />} Ajouter
                      </button>
                      <button onClick={() => setAddMemberOpen(false)} className="text-xs text-gray-400 hover:text-gray-600">Annuler</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setAddMemberOpen(true)}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 mt-1">
                    <FiPlus size={13} /> Ajouter un membre
                  </button>
                )
              )}
            </div>
          )}

          {/* ── GROUPES ── */}
          {tab === 'groups' && (
            <div className="space-y-2">
              {access.groups.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">Aucun groupe associé</p>
              )}
              {access.groups.map(g => (
                <div key={g.group_id} className="flex items-center justify-between py-2 border-b border-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{g.name}</p>
                    <p className="text-xs text-gray-400">{g.member_count} membre{g.member_count !== 1 ? 's' : ''}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(g.group_permissions || []).map(p => <PermBadge key={p} perm={p as Permission} />)}
                    </div>
                  </div>
                  {canManage && (
                    <button onClick={() => removeProjectGroup(projectId, g.group_id).then(load)}
                      className="p-1 text-red-400 hover:text-red-600">
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </div>
              ))}

              {canManage && (
                addGroupOpen ? (
                  <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                    <select value={selGroup} onChange={e => setSelGroup(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400">
                      <option value="">Choisir un groupe...</option>
                      {orgGroups.filter(g => !groupIds.has(g.id)).map(g => (
                        <option key={g.id} value={g.id}>{g.name} ({g.member_count} membres)</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400">Les permissions du groupe s'appliquent automatiquement.</p>
                    <div className="flex gap-2">
                      <button onClick={handleAddGroup} disabled={saving || !selGroup}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg disabled:opacity-50">
                        {saving ? <FiRefreshCw size={11} className="animate-spin" /> : <FiPlus size={11} />} Associer
                      </button>
                      <button onClick={() => setAddGroupOpen(false)} className="text-xs text-gray-400 hover:text-gray-600">Annuler</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setAddGroupOpen(true)}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 mt-1">
                    <FiPlus size={13} /> Associer un groupe
                  </button>
                )
              )}
            </div>
          )}

          {/* ── ÉQUIPES ── */}
          {tab === 'teams' && (
            <div className="space-y-2">
              {access.teams.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">Aucune équipe associée</p>
              )}
              {access.teams.map(t => (
                <div key={t.team_id} className="flex items-center justify-between py-2 border-b border-gray-50">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full bg-${t.color || 'blue'}-500`} />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{t.name}</p>
                      <p className="text-xs text-gray-400">{t.member_count} membre{t.member_count !== 1 ? 's' : ''}</p>
                      <p className="text-xs text-gray-400 italic mt-0.5">Permissions héritées des groupes de chaque membre</p>
                    </div>
                  </div>
                  {canManage && (
                    <button onClick={() => removeProjectTeam(projectId, t.team_id).then(load)}
                      className="p-1 text-red-400 hover:text-red-600">
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </div>
              ))}

              {canManage && (
                addTeamOpen ? (
                  <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                    <select value={selTeam} onChange={e => setSelTeam(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400">
                      <option value="">Choisir une équipe...</option>
                      {orgTeams.filter(t => !teamIds.has(t.id)).map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400">Chaque membre accède au projet avec les permissions de son groupe.</p>
                    <div className="flex gap-2 pt-1">
                      <button onClick={handleAddTeam} disabled={saving || !selTeam}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg disabled:opacity-50">
                        {saving ? <FiRefreshCw size={11} className="animate-spin" /> : <FiPlus size={11} />} Associer
                      </button>
                      <button onClick={() => setAddTeamOpen(false)} className="text-xs text-gray-400 hover:text-gray-600">Annuler</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setAddTeamOpen(true)}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 mt-1">
                    <FiPlus size={13} /> Associer une équipe
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectAccessPanel;
